import { Skel, SkeletonGroup } from '../../../components/SkeletonLoading/Skeleton';
import '../Welcome/index.css';
import '../Plans/index.css';
import '../../../components/MediaManager/MediaManager.css';

/**
 * Skeletons do editar bot com o MESMO formato do conteúdo real de cada aba —
 * quando os dados chegam nada muda de lugar, só "acende".
 *
 * `delayed`: só aparece após ~120ms (fase do cabeçalho, para carga rápida não
 * piscar). Dentro da aba o skeleton aparece na hora, continuando o anterior.
 */

const LABEL = 'Carregando bot';

export function BotHeaderSkeleton({ tabs = 8, delayed = true }) {
    return (
        <SkeletonGroup label={LABEL} delayed={delayed} className="spt-bot-header w-full mt-4">
            <div className="mt-4 w-full py-0 px-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Skel data-skel="selector" w={280} h={40} className="max-w-xs rounded-xl" />
                        <Skel data-skel="market" w={156} h={32} className="rounded-lg" />
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2">
                        <div className="flex items-center gap-2">
                            <Skel data-skel="action" w={36} h={36} className="rounded-xl" />
                            <Skel data-skel="action" w={36} h={36} className="rounded-xl" />
                        </div>
                        <Skel w={190} h={10} />
                        <Skel w={70} h={10} />
                        <Skel w={110} h={10} />
                    </div>
                </div>
            </div>
            <div className="mt-4 lg:border-t lg:border-white/10 px-4 py-3 flex gap-6 overflow-hidden">
                {Array.from({ length: tabs }, (_, i) => (
                    <Skel key={i} data-skel="tab" w={i === 0 ? 76 : 60 + ((i * 13) % 28)} h={12} className="shrink-0" />
                ))}
            </div>
        </SkeletonGroup>
    );
}

// ── Peças ────────────────────────────────────────────────────────────────────

/** Rótulo + campo (input de uma linha ou textarea). */
function Field({ h = 40, label = 110, hint = false, className = 'mt-4' }) {
    return (
        <div className={`${className} space-y-2`}>
            <Skel w={label} h={12} />
            <Skel h={h} className="rounded-xl" />
            {hint && <Skel w="60%" h={9} />}
        </div>
    );
}

/** Card lateral (welcome-card): ícone + título, subtítulo e um controle. */
function SideCard({ lines = 1 }) {
    return (
        <div className="welcome-card">
            <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <Skel w={16} h={16} className="rounded" />
                    <Skel w={110} h={12} />
                </div>
                <Skel w="70%" h={9} />
                {Array.from({ length: lines }, (_, i) => <Skel key={i} h={38} className="rounded-xl" />)}
            </div>
        </div>
    );
}

function SaveButton({ className = 'mt-12' }) {
    return (
        <div className={`flex justify-end ${className}`}>
            <Skel w={120} h={40} className="rounded-xl" />
        </div>
    );
}

/** Linha de toggle — mesma classe do ToggleCard real (anti-clone-toggle). */
function ToggleRow({ title = 150 }) {
    return (
        <div className="anti-clone-toggle">
            <div className="anti-clone-info">
                <Skel w={16} h={16} className="rounded" />
                <Skel w={title} h={12} />
            </div>
            <Skel w={40} h={22} circle />
        </div>
    );
}

/**
 * Mídias (MediaManager): cabeçalho com o modo de envio + blocos no tamanho
 * real (classe media-grid-add: 250×250 no desktop, quadrado de 50% no mobile).
 */
function MediaBlock({ tiles = 2 }) {
    return (
        <div>
            <div className="flex items-center justify-between gap-3">
                <Skel w={120} h={12} />
                <Skel w={170} h={30} className="rounded-lg" />
            </div>
            <div className="media-grid">
                {Array.from({ length: tiles }, (_, i) => (
                    <Skel key={i} w={null} h={null} className="media-grid-add" />
                ))}
            </div>
        </div>
    );
}

/** Mensagem (TextForm): rótulo, barra de formatação, ~8 linhas e dica. */
function MessageBlock({ label = 170, h = 230, className = 'mt-8' }) {
    return (
        <div className={`${className} space-y-2`}>
            <Skel w={label} h={12} />
            <div className="flex gap-2">
                {Array.from({ length: 5 }, (_, i) => <Skel key={i} w={28} h={28} className="rounded-md" />)}
            </div>
            <Skel h={h} className="rounded-xl" />
            <Skel w="55%" h={9} />
        </div>
    );
}

// ── Abas ─────────────────────────────────────────────────────────────────────

function EditBotShape() {
    return (
        <div data-skel-shape="edit">
            <div className="welcome-layout mt-2">
                <div className="spt-form-bot welcome-main">
                    <div className="mt-4 space-y-2">
                        <Skel w={110} h={12} />
                        <div className="flex gap-2">
                            <Skel h={40} className="flex-1 rounded-xl" />
                            <Skel w={36} h={36} className="rounded-xl my-auto" />
                        </div>
                        <Skel w="65%" h={9} />
                    </div>
                    <div className="bot-toggles-group mt-8">
                        <ToggleRow title={170} />
                        <ToggleRow title={190} />
                    </div>
                </div>
                <aside className="welcome-side space-y-4">
                    <SideCard />
                    <SideCard />
                </aside>
            </div>
            <SaveButton />
        </div>
    );
}

function WelcomeShape() {
    return (
        <div data-skel-shape="welcome">
            <div className="welcome-layout">
                <div className="spt-form-bot welcome-form welcome-main">
                    <MediaBlock />
                    <MessageBlock label={190} />
                </div>
                <aside className="welcome-side space-y-4">
                    <SideCard />
                    <SideCard />
                    <SideCard lines={2} />
                </aside>
            </div>
            <SaveButton />
        </div>
    );
}

function PlansShape() {
    return (
        <div data-skel-shape="plans">
            <div className="plans-layout">
                <div className="plans-main panel-card text-white w-full">
                    <div className="p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Skel w={20} h={20} className="rounded" />
                                <div className="space-y-2">
                                    <Skel w={150} h={13} />
                                    <Skel w={240} h={9} />
                                </div>
                            </div>
                            <Skel w={56} h={12} />
                        </div>
                        <div className="space-y-3">
                            {Array.from({ length: 3 }, (_, i) => (
                                <div key={i} className="flex items-center gap-3 rounded-xl border border-white/[0.06] p-3">
                                    <Skel w={28} h={28} className="rounded-lg" />
                                    <div className="flex-1 space-y-2">
                                        <Skel w={`${45 - i * 6}%`} h={12} />
                                        <Skel w="25%" h={9} />
                                    </div>
                                    <Skel w={72} h={28} className="rounded-lg" />
                                </div>
                            ))}
                            <Skel h={40} className="rounded-xl" />
                        </div>
                    </div>
                </div>
                <aside className="plans-side">
                    <SideCard lines={2} />
                </aside>
            </div>
            <SaveButton />
        </div>
    );
}

function PixConfigShape() {
    return (
        <div data-skel-shape="pix" className="spt-form-bot">
            <Skel w={250} h={34} className="rounded-lg mb-6" />
            <MediaBlock tiles={1} />
            <MessageBlock label={160} />
            <Field label={170} className="mt-8" />
            <Field h={44} label={70} className="mt-8" />
            <div className="mt-8 space-y-3">
                <ToggleRow title={110} />
                <ToggleRow title={130} />
            </div>
            <SaveButton />
        </div>
    );
}

/** Checkout: dica, foto redonda + botão, nome com contador e o toggle do pedido. */
function CheckoutShape() {
    return (
        <div data-skel-shape="checkout" className="spt-form-bot">
            <Skel w="55%" h={9} />
            <div className="mt-4 space-y-2">
                <Skel w={120} h={12} />
                <Skel w="65%" h={9} />
                <div className="flex items-center gap-4 pt-1">
                    <Skel w={64} h={64} circle />
                    <Skel w={130} h={38} className="rounded-xl" />
                </div>
            </div>
            <Field h={44} label={130} hint className="mt-6" />
            <div className="mt-6 space-y-2">
                <div className="flex items-center justify-between">
                    <Skel w={240} h={12} />
                    <Skel w={44} h={24} circle />
                </div>
                <Skel w="70%" h={9} />
            </div>
            <SaveButton />
        </div>
    );
}

/** Order Bump: barra "Ativar", nome, mídias, mensagem, preço, botões e entregável. */
function OrderBumpShape() {
    return (
        <div data-skel-shape="order_bump">
            <ToggleRow title={130} />
            <div className="spt-form-bot">
                <Field h={44} label={130} />
                <div className="mt-8"><MediaBlock tiles={1} /></div>
                <MessageBlock label={190} />
                <div className="mt-8 space-y-2">
                    <Skel w={90} h={12} />
                    <Skel w={220} h={44} className="rounded-xl" />
                </div>
                <div className="block md:flex w-full gap-8">
                    {[160, 150].map((label, i) => (
                        <div key={i} className="mt-8 w-full space-y-2">
                            <Skel w={label} h={12} />
                            <div className="flex gap-2">
                                <Skel h={44} className="flex-1 rounded-xl" />
                                <Skel w={44} h={44} className="rounded-xl" />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-8 space-y-2">
                    <Skel w={170} h={12} />
                    <Skel h={130} className="rounded-xl" />
                    <Skel w="60%" h={9} />
                </div>
            </div>
            <SaveButton className="mt-8" />
        </div>
    );
}

/** Upsell / Downsell: tabela à esquerda + card de entrega padrão à direita. */
export function OfferListShape() {
    return (
        <div data-skel-shape="offer_list" className="mt-6 flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1 min-w-0 w-full rounded-xl border border-white/[0.06] overflow-hidden">
                <div className="flex gap-6 px-4 py-3 border-b border-white/[0.06]">
                    {[90, 70, 60, 80].map((w, i) => <Skel key={i} w={w} h={10} />)}
                </div>
                {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} className="flex items-center gap-6 px-4 py-4 border-b border-white/[0.04] last:border-0">
                        <Skel w={`${30 - i * 3}%`} h={12} />
                        <Skel w={60} h={12} />
                        <Skel w={48} h={20} className="rounded-full" />
                        <div className="ml-auto flex gap-2">
                            <Skel w={28} h={28} className="rounded-lg" />
                            <Skel w={28} h={28} className="rounded-lg" />
                        </div>
                    </div>
                ))}
            </div>
            <aside className="w-full lg:w-[340px] shrink-0 space-y-4">
                <SideCard lines={2} />
            </aside>
        </div>
    );
}

const SHAPES = {
    edit: EditBotShape,
    welcome: WelcomeShape,
    plans: PlansShape,
    pix: PixConfigShape,
    checkout: CheckoutShape,
    order_bump: OrderBumpShape,
    upsell: OfferListShape,
    downsell: OfferListShape,
};

/** Skeleton da aba `tab` (cai no de Editar Bot se a aba não tiver um). */
export function TabSkeleton({ tab, delayed = false }) {
    const Shape = SHAPES[tab] || EditBotShape;
    return (
        <SkeletonGroup label={LABEL} delayed={delayed}>
            <Shape />
        </SkeletonGroup>
    );
}

export const EditBotSkeleton = () => <TabSkeleton tab="edit" />;
